import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Select,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import HandshakeRoundedIcon from '@mui/icons-material/HandshakeRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import ShieldRoundedIcon from '@mui/icons-material/ShieldRounded';
import WorkspacePremiumRoundedIcon from '@mui/icons-material/WorkspacePremiumRounded';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import SignupPageShell from './SignupPageShell';
import { Toast } from 'src/components/shared/NotificationToast';
import { UserContext } from './auth/UserContext';
import {
  clearSignupToken,
  getPostSignupRedirect,
  getStoredSignupToken,
  loadRazorpayCheckout,
  persistLoggedInUser,
  storeSignupToken,
} from './auth/signupOnboarding';
import { useCompanyBranding } from './auth/useCompanyBranding';
import useDetectCurrency from 'src/hooks/useDetectCurrency';

const PLAN_ICONS = {
  free_trial: AutoAwesomeRoundedIcon,
  basic_monthly: BoltRoundedIcon,
  basic_yearly: BoltRoundedIcon,
  premium_monthly: WorkspacePremiumRoundedIcon,
  premium_yearly: WorkspacePremiumRoundedIcon,
  solo_monthly: PersonRoundedIcon,
  solo_yearly: PersonRoundedIcon,
  team_monthly: GroupsRoundedIcon,
  team_yearly: GroupsRoundedIcon,
  enterprise_monthly: WorkspacePremiumRoundedIcon,
  enterprise_yearly: WorkspacePremiumRoundedIcon,
  starter_monthly: BoltRoundedIcon,
  starter_yearly: BoltRoundedIcon,
  starter_monthly_usd: BoltRoundedIcon,
  starter_yearly_usd: BoltRoundedIcon,
  pro_monthly: WorkspacePremiumRoundedIcon,
  pro_yearly: WorkspacePremiumRoundedIcon,
  pro_monthly_usd: WorkspacePremiumRoundedIcon,
  pro_yearly_usd: WorkspacePremiumRoundedIcon,
  business_monthly: GroupsRoundedIcon,
  business_yearly: GroupsRoundedIcon,
  business_monthly_usd: GroupsRoundedIcon,
  business_yearly_usd: GroupsRoundedIcon,
};

const CUSTOM_PLAN_CODE = 'custom_plan';

const formatMoney = (amount = 0, currency = 'INR') => {
  const safeAmount = Number(amount) || 0;
  if (safeAmount <= 0) return 'Free';
  const locale = currency === 'USD' ? 'en-US' : 'en-IN';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
  }).format(safeAmount / 100);
};

const resolveSelectedCardCode = (selectedPlan) => {
  if (!selectedPlan?.planCode) return '';
  return String(selectedPlan.planCode || '').trim().toLowerCase();
};

const RegisterPlanSelection = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setUser } = useContext(UserContext);
  const { companyName, companyLogoUrl } = useCompanyBranding();

  const { currency, setCurrency } = useDetectCurrency();

  const [allPlans, setAllPlans] = useState([]);
  const [assignedCustomPlanCode, setAssignedCustomPlanCode] = useState('');
  const [hasAssignedCustomPlan, setHasAssignedCustomPlan] = useState(false);
  const [customPlanRequestStatus, setCustomPlanRequestStatus] = useState('none');
  const [billingInterval, setBillingInterval] = useState('monthly');
  const [selectedPlanCode, setSelectedPlanCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [submittingPlanCode, setSubmittingPlanCode] = useState('');
  const [pressedPlanCode, setPressedPlanCode] = useState('');

  const signupTokenFromUrl = String(searchParams.get('st') || '').trim();
  const signupToken = signupTokenFromUrl || getStoredSignupToken();
  const customPlanWaitPageLink = signupToken
    ? `/auth/register/custom-plan-request?st=${encodeURIComponent(signupToken)}`
    : '/auth/register/custom-plan-request';

  useEffect(() => {
    if (signupTokenFromUrl) storeSignupToken(signupTokenFromUrl);
  }, [signupTokenFromUrl]);

  const fetchPlans = useCallback(async () => {
    if (!signupToken) {
      Toast({ type: 'error', message: 'Complete signup first.' });
      navigate('/auth/register', { replace: true });
      return;
    }
    try {
      const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_API_URL}/auth/register/plans`, {
        params: { signupToken, currency },
      });
      const rawPlans = data.plans || [];
      const paidPlans = rawPlans.filter((p) => p?.planCode && p.category === 'paid');
      const nextCustomPlanCode = String(data.assignedCustomPlanCode || '').trim().toLowerCase();
      const nextHasAssignedCustomPlan = Boolean(data.hasAssignedCustomPlan);

      setAllPlans(paidPlans);
      setAssignedCustomPlanCode(nextCustomPlanCode);
      setHasAssignedCustomPlan(nextHasAssignedCustomPlan);
      setCustomPlanRequestStatus(String(data.customPlanRequestStatus || 'none').trim().toLowerCase() || 'none');

      const preselected = resolveSelectedCardCode(data.selectedPlan);
      if (preselected) setSelectedPlanCode(preselected);
    } catch (error) {
      Toast({ type: 'error', message: error.response?.data?.message || error.message });
      navigate('/auth/register/company', { replace: true });
    } finally {
      setLoading(false);
    }
  }, [navigate, signupToken, currency]);

  useEffect(() => { fetchPlans(); }, [fetchPlans]);

  // Plans visible for the selected billing interval
  const visiblePlans = useMemo(() => {
    if (hasAssignedCustomPlan && assignedCustomPlanCode) {
      return allPlans.filter(
        (p) => (p.isPublic === true && p.billingInterval === billingInterval) || p.planCode === assignedCustomPlanCode
      );
    }
    return allPlans.filter((p) => p.isPublic === true && p.billingInterval === billingInterval);
  }, [allPlans, billingInterval, hasAssignedCustomPlan, assignedCustomPlanCode]);

  // Auto-select first visible plan when interval changes and current selection is no longer visible
  useEffect(() => {
    if (!selectedPlanCode) return;
    const isVisible = visiblePlans.some((p) => p.planCode === selectedPlanCode);
    if (!isVisible && visiblePlans.length > 0) {
      setSelectedPlanCode(visiblePlans[0].planCode);
    }
  }, [visiblePlans, selectedPlanCode]);

  const paidPlanGridColumns = useMemo(() => {
    const totalCards = visiblePlans.length + (hasAssignedCustomPlan ? 0 : 1);
    if (totalCards >= 4) return { xs: '1fr', sm: 'repeat(4, minmax(0, 1fr))' };
    if (totalCards === 3) return { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' };
    return { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' };
  }, [visiblePlans.length, hasAssignedCustomPlan]);

  const finalizeActivatedSignup = async () => {
    const { data } = await axios.post(`${process.env.REACT_APP_BACKEND_API_URL}/auth/register/finalize`, { signupToken });
    clearSignupToken();
    persistLoggedInUser({ data, setUser });
    navigate(getPostSignupRedirect(data.user), { replace: true });
  };

  const startSubscriptionCheckout = async ({ planCode }) => {
    const Razorpay = await loadRazorpayCheckout();
    await axios.post(`${process.env.REACT_APP_BACKEND_API_URL}/auth/register/plan`, {
      signupToken,
      planCode,
    });
    const { data } = await axios.post(`${process.env.REACT_APP_BACKEND_API_URL}/auth/register/checkout`, { signupToken });
    let checkoutCancellationSubmitted = false;

    const cancelCheckoutOnce = async ({ subscriptionId, cancellationReason }) => {
      if (checkoutCancellationSubmitted) return;
      checkoutCancellationSubmitted = true;
      try {
        await axios.post(`${process.env.REACT_APP_BACKEND_API_URL}/auth/register/cancel-payment`, {
          signupToken,
          razorpay_subscription_id: subscriptionId,
          reason: cancellationReason,
        });
      } catch {
        // Allow status polling to reconcile if cancellation sync fails.
      }
    };

    const checkout = new Razorpay({
      key: data.razorpayKeyId,
      subscription_id: data.subscription.id,
      name: 'Quotly',
      description: 'Authenticate card for free trial',
      handler: async (response) => {
        try {
          const verifyResponse = await axios.post(`${process.env.REACT_APP_BACKEND_API_URL}/auth/register/verify-payment`, {
            signupToken,
            ...response,
          });
          clearSignupToken();
          persistLoggedInUser({ data: verifyResponse.data, setUser });
          navigate(getPostSignupRedirect(verifyResponse.data.user), { replace: true });
        } catch (error) {
          const statusCode = error.response?.status;
          const message = error.response?.data?.message || error.message;
          if (statusCode === 409) {
            Toast({ type: 'info', message: 'Subscription verification is still in progress. We will keep checking automatically.' });
          } else {
            Toast({ type: 'error', message });
          }
          navigate('/auth/register/payment-status');
        }
      },
      modal: {
        ondismiss: async () => {
          await cancelCheckoutOnce({ subscriptionId: data.subscription.id, cancellationReason: 'Checkout dismissed by user' });
          navigate('/auth/register/payment-status');
        },
      },
      notes: data.subscription.notes,
      prefill: {},
      theme: { color: '#1976d2' },
    });

    checkout.on('payment.failed', async () => { navigate('/auth/register/payment-status'); });
    checkout.open();
  };

  const startFreeTrialWithoutCard = async ({ planCode }) => {
    await axios.post(`${process.env.REACT_APP_BACKEND_API_URL}/auth/register/plan`, {
      signupToken,
      planCode,
    });
    const { data } = await axios.post(
      `${process.env.REACT_APP_BACKEND_API_URL}/auth/register/activate-free`,
      { signupToken }
    );
    clearSignupToken();
    persistLoggedInUser({ data, setUser });
    navigate(getPostSignupRedirect(data.user), { replace: true });
  };

  const handlePlanAction = async ({ planCode }) => {
    setSubmittingPlanCode(planCode);
    try {
      const selectedPlan = visiblePlans.find((p) => p.planCode === planCode);
      const hasFreeTrial = Number(selectedPlan?.trialDays) > 0 && !selectedPlan?.cardRequired;
      if (hasFreeTrial) {
        await startFreeTrialWithoutCard({ planCode });
      } else {
        await startSubscriptionCheckout({ planCode });
      }
    } catch (error) {
      Toast({ type: 'error', message: error.response?.data?.message || error.message });
      if (error.response?.data?.message === 'Signup is not activated yet') {
        await finalizeActivatedSignup();
      }
    } finally {
      setSubmittingPlanCode('');
    }
  };

  const handleCustomPlanAction = async () => {
    if (customPlanRequestStatus === 'requested' || customPlanRequestStatus === 'accepted' || customPlanRequestStatus === 'done') {
      navigate(customPlanWaitPageLink);
      return;
    }
    try {
      const { data } = await axios.post(`${process.env.REACT_APP_BACKEND_API_URL}/auth/register/custom-plan/request`, { signupToken });
      setCustomPlanRequestStatus(String(data?.status || 'requested').trim().toLowerCase() || 'requested');
      Toast({
        type: 'success',
        message: data?.emailSent === false
          ? 'Custom plan request already exists. Redirecting to the waiting page...'
          : 'Custom plan request sent. Redirecting to the waiting page...',
      });
      navigate(customPlanWaitPageLink);
    } catch (error) {
      Toast({ type: 'error', message: error.response?.data?.message || error.message });
    }
  };

  const triggerPlanSelection = (planCode) => {
    setSelectedPlanCode(planCode);
    setPressedPlanCode(planCode);
    window.setTimeout(() => {
      setPressedPlanCode((current) => (current === planCode ? '' : current));
    }, 180);
  };

  if (loading) {
    return (
      <SignupPageShell title="Choose Plan" description="Choose your plan">
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      </SignupPageShell>
    );
  }

  return (
    <SignupPageShell title="Choose Plan" description="Choose your plan" maxWidth="1180px" fullWidth>
      <Stack spacing={3}>
        {/* Billing interval toggle */}
        <Stack spacing={2} alignItems="center">
          <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={{ xs: 1, sm: 1.2 }}
              sx={{
                px: { xs: 1.2, sm: 1.5 },
                py: { xs: 0.7, sm: 0.85 },
                borderRadius: 999,
                border: '1px solid rgba(148,163,184,0.24)',
                background: 'linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(248,250,252,0.98) 100%)',
                boxShadow: '0 16px 34px rgba(15, 23, 42, 0.08)',
                flexWrap: 'wrap',
                justifyContent: 'center',
              }}
            >
              <ToggleButtonGroup
                exclusive
                value={billingInterval}
                onChange={(_, nextValue) => { if (nextValue) setBillingInterval(nextValue); }}
                aria-label="Billing interval"
                sx={{
                  position: 'relative',
                  isolation: 'isolate',
                  width: { xs: 230, sm: 270 },
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  backgroundColor: 'rgba(241,245,249,0.9)',
                  borderRadius: 999,
                  p: 0.35,
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 3,
                    bottom: 3,
                    left: 3,
                    width: 'calc(50% - 3px)',
                    borderRadius: 999,
                    backgroundColor: '#ffffff',
                    boxShadow: '0 6px 18px rgba(15, 23, 42, 0.12)',
                    transform: billingInterval === 'yearly' ? 'translateX(100%)' : 'translateX(0%)',
                    transition: 'transform 220ms ease',
                    zIndex: 0,
                  },
                  '& .MuiToggleButtonGroup-grouped': {
                    border: 0,
                    borderRadius: '999px !important',
                    color: 'text.secondary',
                    position: 'relative',
                    zIndex: 1,
                    fontSize: { xs: '0.88rem', sm: '0.98rem' },
                    fontWeight: 700,
                    lineHeight: 1.1,
                    minWidth: 0,
                    px: { xs: 1.8, sm: 2.2 },
                    py: { xs: 0.85, sm: 1 },
                    textTransform: 'none',
                    backgroundColor: 'transparent',
                    transition: 'color 220ms ease',
                  },
                  '& .MuiToggleButtonGroup-grouped.Mui-selected': {
                    color: '#111827',
                    backgroundColor: 'transparent',
                  },
                  '& .MuiToggleButtonGroup-grouped.Mui-selected:hover': {
                    backgroundColor: 'transparent',
                  },
                  '& .MuiToggleButtonGroup-grouped:not(.Mui-selected):hover': {
                    backgroundColor: 'rgba(255,255,255,0.5)',
                  },
                }}
              >
                <ToggleButton value="monthly" aria-label="Monthly billing">Monthly</ToggleButton>
                <ToggleButton value="yearly" aria-label="Yearly billing">Yearly</ToggleButton>
              </ToggleButtonGroup>
              <Chip
                label="Save 20%"
                size="small"
                color="success"
                sx={{ fontWeight: 800, fontSize: '0.68rem', height: 22 }}
              />

              {/* Currency selector */}
              <Select
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                size="small"
                sx={{ fontSize: '0.8rem', fontWeight: 700, minWidth: 90, height: 32, ml: { xs: 0, sm: 1 } }}
              >
                <MenuItem value="INR">₹ INR</MenuItem>
                <MenuItem value="USD">$ USD</MenuItem>
              </Select>
            </Stack>
          </Box>

          {/* Plan cards grid */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: paidPlanGridColumns,
              gap: 2,
              mt: { xs: 3, md: 4 },
              alignItems: 'stretch',
              width: '100%',
            }}
          >
            {visiblePlans.map((plan) => {
              const isSelected = selectedPlanCode === plan.planCode;
              const isSubmitting = submittingPlanCode === plan.planCode;
              const isAssignedCustom = hasAssignedCustomPlan && plan.planCode === assignedCustomPlanCode && plan.isPublic !== true;
              const PlanIcon = isAssignedCustom ? HandshakeRoundedIcon : (PLAN_ICONS[plan.planCode] || WorkspacePremiumRoundedIcon);
              const isRecommended = plan.isRecommended === true;
              const planActionLabel = isAssignedCustom ? 'Start Subscription' : 'Start Free Trial';

              return (
                <Box
                  key={plan.planCode}
                  onClick={() => triggerPlanSelection(plan.planCode)}
                  sx={{
                    width: '100%',
                    minHeight: { xs: 'auto', lg: 390 },
                    border: 1,
                    borderColor: isSelected ? 'primary.main' : isRecommended ? 'rgba(25, 118, 210, 0.35)' : 'divider',
                    borderRadius: 2.5,
                    p: 2.5,
                    cursor: 'pointer',
                    background: isSelected
                      ? 'linear-gradient(180deg, #f2f8ff 0%, #ffffff 100%)'
                      : isRecommended
                        ? 'linear-gradient(180deg, #f7fbff 0%, #ffffff 100%)'
                        : 'linear-gradient(180deg, #ffffff 0%, #fbfdff 100%)',
                    boxShadow: isSelected
                      ? '0 22px 52px rgba(25, 118, 210, 0.18)'
                      : isRecommended
                        ? '0 18px 42px rgba(25, 118, 210, 0.12)'
                        : '0 14px 30px rgba(15, 23, 42, 0.08)',
                    overflow: 'hidden',
                    position: 'relative',
                    isolation: 'isolate',
                    transform: pressedPlanCode === plan.planCode
                      ? 'scale(0.975)'
                      : isSelected
                        ? 'translateY(-6px) scale(1.02)'
                        : 'translateY(0) scale(1)',
                    transition: 'transform 180ms ease, box-shadow 220ms ease, border-color 220ms ease, background-color 220ms ease',
                    '&:hover': {
                      transform: pressedPlanCode === plan.planCode
                        ? 'scale(0.975)'
                        : isSelected
                          ? 'translateY(-8px) scale(1.02)'
                          : 'translateY(-4px) scale(1.01)',
                      boxShadow: isSelected
                        ? '0 28px 58px rgba(25, 118, 210, 0.22)'
                        : isRecommended
                          ? '0 24px 46px rgba(25, 118, 210, 0.16)'
                          : '0 20px 38px rgba(15, 23, 42, 0.12)',
                    },
                    '&:before': {
                      content: '""',
                      position: 'absolute',
                      inset: 0,
                      background: isSelected
                        ? 'linear-gradient(180deg, rgba(25,118,210,0.10), rgba(25,118,210,0.02))'
                        : 'linear-gradient(180deg, rgba(148,163,184,0.08), rgba(148,163,184,0.02))',
                      pointerEvents: 'none',
                    },
                  }}
                >
                  {isRecommended ? (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 18,
                        right: -38,
                        transform: 'rotate(35deg)',
                        background: 'linear-gradient(135deg, #1d4ed8, #38bdf8)',
                        color: 'common.white',
                        px: 5,
                        py: 0.75,
                        fontSize: 12,
                        fontWeight: 700,
                        letterSpacing: 0.7,
                        textTransform: 'uppercase',
                        boxShadow: '0 10px 20px rgba(29, 78, 216, 0.28)',
                        zIndex: 3,
                      }}
                    >
                      Recommended
                    </Box>
                  ) : null}

                  <Stack spacing={1.5} sx={{ position: 'relative', height: '100%' }}>
                    <Box
                      component="img"
                      src={companyLogoUrl}
                      alt={companyName}
                      sx={{
                        position: 'absolute',
                        right: -10,
                        top: 34,
                        width: 104,
                        height: 104,
                        objectFit: 'contain',
                        opacity: 0.06,
                        filter: 'grayscale(100%)',
                        pointerEvents: 'none',
                        zIndex: 0,
                      }}
                    />

                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: 3,
                          display: 'grid',
                          placeItems: 'center',
                          color: isSelected ? 'primary.main' : 'text.primary',
                          background: isSelected
                            ? 'linear-gradient(135deg, rgba(25,118,210,0.16), rgba(59,130,246,0.08))'
                            : 'linear-gradient(135deg, rgba(148,163,184,0.18), rgba(148,163,184,0.08))',
                          border: '1px solid rgba(148,163,184,0.18)',
                        }}
                      >
                        <PlanIcon />
                      </Box>
                    </Stack>

                    <Box>
                      <Typography variant="h4" mb={0.75}>{plan.planName}</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, mb: 0.25 }}>
                        <Typography variant="h3">{formatMoney(plan.amount, plan.currency)}</Typography>
                        {plan.amount > 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            /{plan.billingInterval === 'yearly' ? 'year' : 'month'}
                          </Typography>
                        ) : null}
                      </Box>
                      {plan.billingInterval === 'yearly' && plan.amount > 0 ? (
                        <Typography variant="caption" color="success.main" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>
                          {formatMoney(Math.round(plan.amount / 12), plan.currency)}/month equivalent
                        </Typography>
                      ) : null}
                      {plan.description ? (
                        <Typography color="text.secondary" sx={{ fontSize: 15, mt: 0.75 }}>
                          {plan.description}
                        </Typography>
                      ) : null}
                    </Box>

                    <Box
                      sx={{
                        height: 2,
                        borderRadius: 999,
                        background: isRecommended
                          ? 'linear-gradient(90deg, rgba(56,189,248,0.18), rgba(29,78,216,0.7), rgba(56,189,248,0.18))'
                          : 'linear-gradient(90deg, rgba(148,163,184,0.14), rgba(148,163,184,0.45), rgba(148,163,184,0.14))',
                      }}
                    />

                    <Stack spacing={1} mb={0.5}>
                      {(plan.features || []).map((feature, index) => (
                        <Stack
                          key={(typeof feature === 'string' ? feature : (feature.key || feature.label)) || index}
                          direction="row"
                          spacing={1.1}
                          alignItems="center"
                        >
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              background: isRecommended
                                ? 'linear-gradient(135deg, #1d4ed8, #38bdf8)'
                                : 'linear-gradient(135deg, #64748b, #94a3b8)',
                              flexShrink: 0,
                            }}
                          />
                          <Typography variant="body2" color="text.secondary">
                            {typeof feature === 'string' ? feature : (feature.label || '')}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>

                    <Box
                      sx={{
                        mt: 'auto',
                        position: 'relative',
                        '&:before': isSelected ? {
                          content: '""',
                          position: 'absolute',
                          inset: -10,
                          borderRadius: 4,
                          background: 'radial-gradient(circle at center, rgba(59,130,246,0.28), rgba(56,189,248,0.12) 42%, transparent 74%)',
                          filter: 'blur(10px)',
                          zIndex: 0,
                        } : undefined,
                        minHeight: 64,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-end',
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, px: 0.5, py: 0.25, position: 'relative', zIndex: 1 }}>
                        {isAssignedCustom ? (
                          <HandshakeRoundedIcon sx={{ fontSize: 18, color: 'secondary.main' }} />
                        ) : (
                          <ShieldRoundedIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                        )}
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                          {isAssignedCustom
                            ? 'Custom subscription checkout with Razorpay'
                            : 'Free trial uses card authentication only.'}
                        </Typography>
                      </Stack>
                      <Button
                        fullWidth
                        variant={isSelected ? 'contained' : 'outlined'}
                        onClick={(event) => {
                          event.stopPropagation();
                          triggerPlanSelection(plan.planCode);
                          handlePlanAction({ planCode: plan.planCode });
                        }}
                        disabled={Boolean(submittingPlanCode)}
                        sx={{
                          py: 1.05,
                          position: 'relative',
                          zIndex: 1,
                          boxShadow: isSelected ? '0 14px 30px rgba(25, 118, 210, 0.28)' : 'none',
                        }}
                      >
                        {isSubmitting ? 'Processing...' : planActionLabel}
                      </Button>
                    </Box>
                  </Stack>
                </Box>
              );
            })}

            {/* Contact Sales card — hidden when assigned custom plan is active */}
            {!hasAssignedCustomPlan ? (
              <Box
                onClick={() => triggerPlanSelection(CUSTOM_PLAN_CODE)}
                sx={{
                  width: '100%',
                  minHeight: { xs: 'auto', lg: 390 },
                  border: 1,
                  borderColor: selectedPlanCode === CUSTOM_PLAN_CODE ? 'secondary.main' : 'divider',
                  borderRadius: 2.5,
                  p: 2.5,
                  cursor: 'pointer',
                  background: selectedPlanCode === CUSTOM_PLAN_CODE
                    ? 'linear-gradient(180deg, #fdf4ff 0%, #ffffff 100%)'
                    : 'linear-gradient(180deg, #ffffff 0%, #fbfdff 100%)',
                  boxShadow: selectedPlanCode === CUSTOM_PLAN_CODE
                    ? '0 22px 52px rgba(156, 39, 176, 0.12)'
                    : '0 14px 30px rgba(15, 23, 42, 0.08)',
                  overflow: 'hidden',
                  position: 'relative',
                  isolation: 'isolate',
                  transform: pressedPlanCode === CUSTOM_PLAN_CODE
                    ? 'scale(0.975)'
                    : selectedPlanCode === CUSTOM_PLAN_CODE
                      ? 'translateY(-6px) scale(1.02)'
                      : 'translateY(0) scale(1)',
                  transition: 'transform 180ms ease, box-shadow 220ms ease, border-color 220ms ease, background-color 220ms ease',
                  '&:hover': {
                    transform: pressedPlanCode === CUSTOM_PLAN_CODE
                      ? 'scale(0.975)'
                      : selectedPlanCode === CUSTOM_PLAN_CODE
                        ? 'translateY(-8px) scale(1.02)'
                        : 'translateY(-4px) scale(1.01)',
                    boxShadow: selectedPlanCode === CUSTOM_PLAN_CODE
                      ? '0 28px 58px rgba(156, 39, 176, 0.16)'
                      : '0 20px 38px rgba(15, 23, 42, 0.12)',
                  },
                  '&:before': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    background: selectedPlanCode === CUSTOM_PLAN_CODE
                      ? 'linear-gradient(180deg, rgba(156,39,176,0.07), rgba(156,39,176,0.02))'
                      : 'linear-gradient(180deg, rgba(148,163,184,0.08), rgba(148,163,184,0.02))',
                    pointerEvents: 'none',
                  },
                }}
              >
                <Stack spacing={1.5} sx={{ position: 'relative', height: '100%' }}>
                  <Box
                    component="img"
                    src={companyLogoUrl}
                    alt={companyName}
                    sx={{
                      position: 'absolute',
                      right: -10,
                      top: 34,
                      width: 104,
                      height: 104,
                      objectFit: 'contain',
                      opacity: 0.06,
                      filter: 'grayscale(100%)',
                      pointerEvents: 'none',
                      zIndex: 0,
                    }}
                  />

                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 3,
                        display: 'grid',
                        placeItems: 'center',
                        color: selectedPlanCode === CUSTOM_PLAN_CODE ? 'secondary.main' : 'text.primary',
                        background: selectedPlanCode === CUSTOM_PLAN_CODE
                          ? 'linear-gradient(135deg, rgba(156,39,176,0.16), rgba(186,104,200,0.08))'
                          : 'linear-gradient(135deg, rgba(148,163,184,0.18), rgba(148,163,184,0.08))',
                        border: '1px solid rgba(148,163,184,0.18)',
                      }}
                    >
                      <HandshakeRoundedIcon />
                    </Box>
                  </Stack>

                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, mb: 0.25 }}>
                      <Typography variant="h3">Custom pricing</Typography>
                    </Box>
                    <Typography color="text.secondary" sx={{ fontSize: 15, mt: 0.75 }}>
                      Talk to our team for custom pricing, onboarding, and workspace limits tailored to your business.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      height: 2,
                      borderRadius: 999,
                      background: 'linear-gradient(90deg, rgba(148,163,184,0.14), rgba(148,163,184,0.45), rgba(148,163,184,0.14))',
                    }}
                  />

                  <Stack spacing={1} mb={0.5}>
                    {[
                      'Custom pricing and onboarding',
                      'Tailored workflow and quote volume',
                      'Customized deployment option',
                    ].map((feature) => (
                      <Stack key={feature} direction="row" spacing={1.1} alignItems="center">
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg, #64748b, #94a3b8)',
                            flexShrink: 0,
                          }}
                        />
                        <Typography variant="body2" color="text.secondary">{feature}</Typography>
                      </Stack>
                    ))}
                  </Stack>

                  <Box
                    sx={{
                      mt: 'auto',
                      minHeight: 64,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'flex-end',
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, px: 0.5, py: 0.25 }}>
                      <HandshakeRoundedIcon sx={{ fontSize: 18, color: 'secondary.main' }} />
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        {customPlanRequestStatus === 'requested' || customPlanRequestStatus === 'accepted'
                          ? 'Request already sent — our team will reach out.'
                          : 'Contact sales for a tailored workspace setup'}
                      </Typography>
                    </Stack>
                    <Button
                      fullWidth
                      variant={selectedPlanCode === CUSTOM_PLAN_CODE ? 'contained' : 'outlined'}
                      color="secondary"
                      onClick={(event) => {
                        event.stopPropagation();
                        triggerPlanSelection(CUSTOM_PLAN_CODE);
                        handleCustomPlanAction();
                      }}
                      disabled={customPlanRequestStatus === 'requested' || customPlanRequestStatus === 'accepted'}
                      sx={{ py: 1.05 }}
                    >
                      {customPlanRequestStatus === 'requested' || customPlanRequestStatus === 'accepted'
                        ? 'Request Sent'
                        : 'Contact Sales'}
                    </Button>
                  </Box>
                </Stack>
              </Box>
            ) : null}
          </Box>
        </Stack>

        <Alert severity="info" sx={{ py: 0.25 }}>
          If checkout is interrupted, use the payment status page to resume or confirm activation.
        </Alert>
        <Stack direction="row" justifyContent="space-between">
          <Button component={Link} to="/auth/login">Back</Button>
          <Button component={Link} to="/auth/register/payment-status">Payment Status</Button>
        </Stack>
      </Stack>
    </SignupPageShell>
  );
};

export default RegisterPlanSelection;
